import React from 'react'
import { Paper, Box, Typography, Stack, TextField, MenuItem } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material'

const EventDetailsForm = ({
  eventNameEN,
  organizer,
  slugEvent,
  codeEvent,
  file,
  existingLogo,
  organizersData,
  userRole,
  onEventNameChange,
  onOrganizerChange,
  onSlugChange,
  onCodeChange,
  onFileSelect,
  onDragOver,
  onDrop,
}) => {
  const hasNewFile = file && file.name
  const hasExistingLogo = existingLogo && typeof existingLogo === 'string' && existingLogo.trim() !== ''

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2, md: 2.5 },
        mb: { xs: 1.5, sm: 2, md: 2.5 },
        backgroundColor: 'background.paper',
        border: (theme) =>
          `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
        borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
        boxShadow: (theme) =>
          theme.palette.mode === 'light'
            ? '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)'
            : '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.4)',
        transition: (theme) =>
          theme.transitions.create(['box-shadow', 'border-color'], {
            duration: theme.transitions.duration.short,
          }),
        '&:hover': {
          boxShadow: (theme) =>
            theme.palette.mode === 'light'
              ? '0 2px 6px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.12)'
              : '0 2px 6px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.5)',
        },
      }}>
      <Box sx={{ mb: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            mb: { xs: 0.25, sm: 0.375, md: 0.5 },
            color: 'text.primary',
            fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.125rem' },
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
          }}>
          Event Details
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' },
          }}>
          Basic information about your event
        </Typography>
      </Box>

      <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
          sx={{ width: '100%' }}>
          <TextField
            label="Event Name"
            placeholder="Enter event name"
            value={eventNameEN}
            onChange={onEventNameChange}
            required
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(0, 0, 0, 0.02)'
                    : 'rgba(255, 255, 255, 0.05)',
                transition: (theme) =>
                  theme.transitions.create(['background-color', 'box-shadow'], {
                    duration: theme.transitions.duration.short,
                  }),
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'rgba(255, 255, 255, 0.08)',
                },
                '&.Mui-focused': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'rgba(255, 255, 255, 0.08)',
                  boxShadow: (theme) =>
                    theme.palette.mode === 'light'
                      ? '0 0 0 2px rgba(52, 103, 246, 0.1)'
                      : '0 0 0 2px rgba(144, 202, 249, 0.15)',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                fontWeight: 500,
              },
            }}
          />

          <TextField
            select
            label="Select Organizer"
            value={organizer}
            onChange={onOrganizerChange}
            required
            fullWidth
            size="small"
            disabled={userRole !== 'Admin'}
            InputLabelProps={{
              shrink: true,
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected || selected === '') {
                  return (
                    <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>Select Organizer</span>
                  )
                }
                const selectedOrganizer = organizersData?.organizerUsers?.find(
                  (org) => org._id === selected
                )
                return selectedOrganizer?.name || ''
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(0, 0, 0, 0.02)'
                    : 'rgba(255, 255, 255, 0.05)',
                transition: (theme) =>
                  theme.transitions.create(['background-color', 'box-shadow'], {
                    duration: theme.transitions.duration.short,
                  }),
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'rgba(255, 255, 255, 0.08)',
                },
                '&.Mui-focused': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'rgba(255, 255, 255, 0.08)',
                  boxShadow: (theme) =>
                    theme.palette.mode === 'light'
                      ? '0 0 0 2px rgba(52, 103, 246, 0.1)'
                      : '0 0 0 2px rgba(144, 202, 249, 0.15)',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                fontWeight: 500,
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
              },
              '& .MuiSelect-select': {
                padding: {
                  xs: '10.5px 14px',
                  sm: '11px 14px',
                  md: '11.5px 14px',
                },
                paddingRight: { xs: '38px', sm: '42px', md: '46px' },
                '&:focus': {
                  backgroundColor: 'transparent',
                },
              },
              '& .MuiSelect-icon': {
                right: { xs: 10, sm: 12, md: 14 },
                color: 'text.secondary',
                fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
              },
            }}>
            <MenuItem value="" disabled>
              <em>Select Organizer</em>
            </MenuItem>
            {organizersData?.organizerUsers
              ? organizersData.organizerUsers.map((row) => (
                  <MenuItem key={row._id} value={row._id}>
                    {row.name}
                  </MenuItem>
                ))
              : (
                  <MenuItem value="placeholder">Placeholder</MenuItem>
                )}
          </TextField>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
          sx={{ width: '100%' }}>
          <TextField
            label="Slug Event"
            placeholder="charity-run-2025"
            value={slugEvent}
            onChange={onSlugChange}
            required
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(0, 0, 0, 0.02)'
                    : 'rgba(255, 255, 255, 0.05)',
                transition: (theme) =>
                  theme.transitions.create(['background-color', 'box-shadow'], {
                    duration: theme.transitions.duration.short,
                  }),
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'rgba(255, 255, 255, 0.08)',
                },
                '&.Mui-focused': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'rgba(255, 255, 255, 0.08)',
                  boxShadow: (theme) =>
                    theme.palette.mode === 'light'
                      ? '0 0 0 2px rgba(52, 103, 246, 0.1)'
                      : '0 0 0 2px rgba(144, 202, 249, 0.15)',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                fontWeight: 500,
              },
            }}
          />

          <TextField
            label="Code Event"
            placeholder="Enter event code"
            value={codeEvent}
            onChange={onCodeChange}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(0, 0, 0, 0.02)'
                    : 'rgba(255, 255, 255, 0.05)',
                transition: (theme) =>
                  theme.transitions.create(['background-color', 'box-shadow'], {
                    duration: theme.transitions.duration.short,
                  }),
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'rgba(255, 255, 255, 0.08)',
                },
                '&.Mui-focused': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'rgba(255, 255, 255, 0.08)',
                  boxShadow: (theme) =>
                    theme.palette.mode === 'light'
                      ? '0 0 0 2px rgba(52, 103, 246, 0.1)'
                      : '0 0 0 2px rgba(144, 202, 249, 0.15)',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                fontWeight: 500,
              },
            }}
          />
        </Stack>

        <Box
          component="label"
          onDragOver={onDragOver}
          onDrop={onDrop}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            border: (theme) =>
              `2px dashed ${theme.palette.mode === 'light' ? 'rgba(52, 103, 246, 0.3)' : 'rgba(144, 202, 249, 0.4)'}`,
            borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
            p: { xs: 2, sm: 2.5, md: 3 },
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? 'rgba(52, 103, 246, 0.02)'
                : 'rgba(144, 202, 249, 0.05)',
            cursor: 'pointer',
            transition: (theme) =>
              theme.transitions.create(['border-color', 'background-color', 'transform'], {
                duration: theme.transitions.duration.short,
              }),
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(52, 103, 246, 0.05)'
                  : 'rgba(144, 202, 249, 0.1)',
              transform: 'translateY(-1px)',
              boxShadow: (theme) =>
                theme.palette.mode === 'light'
                  ? '0 2px 6px rgba(52, 103, 246, 0.15)'
                  : '0 2px 6px rgba(144, 202, 249, 0.2)',
            },
          }}>
          <input
            onChange={onFileSelect}
            accept="image/*"
            style={{ display: 'none' }}
            id="file-upload-edit"
            type="file"
          />
          {hasNewFile ? (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <CheckCircleIcon
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  color: 'success.main',
                  mb: { xs: 0.75, sm: 1, md: 1.25 },
                }}
              />
              <Typography
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  mb: { xs: 0.25, sm: 0.375, md: 0.5 },
                }}>
                {file.name}
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' },
                }}>
                Click to change or drag and drop a new file
              </Typography>
            </Box>
          ) : hasExistingLogo ? (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Box
                component="img"
                src={existingLogo}
                alt="Current logo"
                sx={{
                  maxWidth: 120,
                  maxHeight: 120,
                  objectFit: 'contain',
                  mb: 1,
                  borderRadius: 1,
                }}
              />
              <Typography
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  mb: 0.25,
                }}>
                Current logo
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' },
                }}>
                Click to replace or drag and drop a new file
              </Typography>
            </Box>
          ) : (
            <>
              <CloudUploadIcon
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  color: 'primary.main',
                  mb: { xs: 1, sm: 1.25, md: 1.5 },
                  opacity: 0.8,
                }}
              />
              <Typography
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  mb: { xs: 0.375, sm: 0.5, md: 0.75 },
                  textAlign: 'center',
                }}>
                Click to upload or drag and drop
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' },
                  textAlign: 'center',
                }}>
                SVG, PNG, JPG, or GIF (MAX. 800x800px)
              </Typography>
            </>
          )}
        </Box>
      </Stack>
    </Paper>
  )
}

export default EventDetailsForm
